import { REALTIME_SUBSCRIBE_STATES } from '@supabase/supabase-js'
import { invoke } from '@vueuse/core'

export default defineNuxtPlugin(() => {
  const supabase = useSupabaseClient<Supabase>()
  const { isLoaded } = useSession()
  const data = useData()
  const focused = useWindowFocus()
  const tables = new Set([
    'admins',
    'events',
    'exhibitors',
    'extras',
    'feed',
    'payments',
    'payments_exhibitors',
    'payments_show_objects',
    'payments_extras',
    'scope_requests',
    'show_objects',
    'shows',
    'subusers',
  ])
  let consecutiveFailures = 0
  let lastStatus: REALTIME_SUBSCRIBE_STATES | undefined
  const isConnected = ref(false)

  async function subscribe() {
    await supabase.realtime.setAuth(null)
    supabase.realtime.connect()

    const subscription = supabase
      .channel('changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
        },
        async (payload) => {
          const auth = useAuthStore()

          // TODO: update events_entry_count
          try {
            if (payload.table === 'users') {
              const auth = useAuthStore()

              if (
                payload.eventType === 'DELETE' &&
                auth.userId === payload.old.id
              ) {
                await auth.signOut()
              } else if ((payload.new as PortalUser).id === auth.userId) {
                auth.user = payload.new as PortalUser
              }
            } else if (payload.table === 'scopes') {
              if (
                (payload.eventType === 'INSERT' ||
                  payload.eventType === 'UPDATE') &&
                (payload.new.scoped_user_id === auth.userId ||
                  payload.new.user_id === auth.userId)
              ) {
                /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
                ;(data as any).scopes.set(
                  payload.new.resource_id,
                  payload.new as PortalScope,
                )
              } else {
                /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
                ;(data as any).scopes.delete(
                  (payload.old as PortalScope).resource_id,
                )
              }
            } else if (tables.has(payload.table)) {
              if (payload.eventType === 'DELETE') {
                /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
                ;(data as any)[payload.table].delete(payload.old.id)
              } else {
                /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
                ;(data as any)[payload.table].set(payload.new.id, payload.new)
              }
            }
          } catch (error) {
            console.error(error)
          }
        },
      )
      .subscribe((status, error) => {
        if (lastStatus && status === lastStatus) {
          return
        }

        lastStatus = status

        isConnected.value = status === REALTIME_SUBSCRIBE_STATES.SUBSCRIBED

        if (isConnected.value) {
          consecutiveFailures = 0
          return
        }

        if (error) {
          console.error(error)
        }

        supabase.realtime.flushSendBuffer()
        subscription.untrack()
        subscription.unsubscribe()
        supabase.realtime.disconnect()

        if (!focused.value) {
          return
        }

        if (consecutiveFailures < 10) {
          setTimeout(
            () => {
              subscribe()
            },
            Math.min(10_000, 250 * Math.pow(2, consecutiveFailures++)),
          )
        }
      })
  }

  // Wait for the session state to be loaded to capture the user's auth token
  invoke(async () => {
    await until(isLoaded).toBeTruthy()

    whenever(
      () => focused.value && !isConnected.value,
      () => {
        subscribe()
      },
      { immediate: true },
    )
  })
})
